import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Book, Truck, CheckCircle, ArrowLeft, ArrowRight, MapPin, Package, CreditCard, Gift, Users, Minus, Plus, Eye, Star } from "lucide-react";

interface PrintBookOrderProps {
  storyId: number;
  storyTitle?: string;
}

interface BookConfig {
  id: number;
  name: string;
  podPackageId: string;
  size: string;
  binding: string;
  pages: number;
  color: string;
  basePrice: string;
  retailPrice: string;
}

interface ShippingAddress {
  name: string;
  email: string;
  address_line1: string;
  address_line2?: string;
  city: string;
  state_code: string;
  country_code: string;
  postal_code: string;
  phone?: string;
}

interface FormErrors {
  [key: string]: string;
}

type OrderStep = 'book-config' | 'shipping' | 'review';

export function PrintBookOrder({ storyId, storyTitle }: PrintBookOrderProps) {
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState<OrderStep>('book-config');
  const [selectedConfig, setSelectedConfig] = useState<string>("");
  const [bookType, setBookType] = useState<"storybook" | "coloring">("storybook");
  const [quantity, setQuantity] = useState(1);
  const [isGift, setIsGift] = useState(false);
  const [giftMessage, setGiftMessage] = useState("");
  const [shippingLevel, setShippingLevel] = useState<string>("Mail");
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [shippingAddress, setShippingAddress] = useState<ShippingAddress>({
    name: "",
    email: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state_code: "",
    country_code: "US",
    postal_code: "",
    phone: "",
  });

  // Auto-format postal code based on country
  const formatPostalCode = (value: string, country: string) => {
    if (country === 'US') {
      const cleanValue = value.replace(/\D/g, '');
      if (cleanValue.length >= 5) {
        return cleanValue.length > 5 
          ? `${cleanValue.slice(0, 5)}-${cleanValue.slice(5, 9)}`
          : cleanValue.slice(0, 5);
      }
      return cleanValue;
    } else if (country === 'CA') {
      // Canadian postal codes: A1A 1A1
      const formatted = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
      if (formatted.length >= 3) {
        return formatted.length > 3 
          ? `${formatted.slice(0, 3)} ${formatted.slice(3, 6)}`
          : formatted.slice(0, 3);
      }
      return formatted;
    }
    // For other countries, return as-is but trim whitespace
    return value.trim();
  };

  // Validate form fields
  const validateField = (field: string, value: string) => {
    const errors = { ...formErrors };
    
    switch (field) {
      case 'name':
        if (!value.trim()) errors.name = 'Full name is required';
        else if (value.trim().length < 2) errors.name = 'Name must be at least 2 characters';
        else delete errors.name;
        break;
      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (value && !emailRegex.test(value)) errors.email = 'Please enter a valid email address';
        else delete errors.email;
        break;
      case 'address_line1':
        if (!value.trim()) errors.address_line1 = 'Street address is required';
        else delete errors.address_line1;
        break;
      case 'city':
        if (!value.trim()) errors.city = 'City is required';
        else delete errors.city;
        break;
      case 'postal_code':
        if (!value.trim()) errors.postal_code = 'Postal code is required';
        else if (shippingAddress.country_code === 'US' && !/^\d{5}(-\d{4})?$/.test(value)) {
          errors.postal_code = 'Please enter a valid US postal code';
        } else if (shippingAddress.country_code === 'CA' && !/^[A-Z]\d[A-Z] \d[A-Z]\d$/.test(value)) {
          errors.postal_code = 'Please enter a valid Canadian postal code (A1A 1A1)';
        } else delete errors.postal_code;
        break;
      case 'phone':
        if (value) {
          if (shippingAddress.country_code === 'US') {
            // US phone number validation
            const digits = value.replace(/\D/g, '');
            if (digits.length !== 10) {
              errors.phone = 'Please enter a valid 10-digit US phone number';
            } else delete errors.phone;
          } else {
            // International phone number validation (more lenient)
            if (!/^[\+]?[1-9][\d\-\s\(\)]{7,15}$/.test(value.replace(/\s/g, ''))) {
              errors.phone = 'Please enter a valid phone number';
            } else delete errors.phone;
          }
        } else delete errors.phone;
        break;
      default:
        break;
    }
    
    setFormErrors(errors);
  };

  // Step validation
  const canProceedToNextStep = () => {
    switch (currentStep) {
      case 'book-config':
        return selectedConfig && bookType;
      case 'shipping':
        const requiredFields = ['name', 'address_line1', 'city', 'postal_code'];
        const hasRequiredFields = requiredFields.every(field => 
          shippingAddress[field as keyof ShippingAddress]?.toString().trim()
        );
        const hasNoErrors = Object.keys(formErrors).length === 0;
        return hasRequiredFields && hasNoErrors;
      case 'review':
        return true;
      default:
        return false;
    }
  };

  // Step navigation
  const nextStep = () => {
    if (currentStep === 'book-config') setCurrentStep('shipping');
    else if (currentStep === 'shipping') setCurrentStep('review');
  };

  const previousStep = () => {
    if (currentStep === 'review') setCurrentStep('shipping');
    else if (currentStep === 'shipping') setCurrentStep('book-config');
  };

  // Reset form when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setCurrentStep('book-config');
      setFormErrors({});
    }
  }, [isOpen]);

  // Fetch available book configurations
  const { data: bookConfigs, isLoading: configsLoading } = useQuery<{
    success: boolean;
    configs: BookConfig[];
  }>({
    queryKey: ["/api/lulu/book-configs"],
    // Remove enabled restriction so configs load immediately
  });

  // Get price estimate
  const { data: priceEstimate, isLoading: priceLoading } = useQuery<{
    success: boolean;
    estimate: {
      currency: string;
      total_cost: number;
      production_cost: number;
      shipping_cost: number;
    };
  }>({
    queryKey: ["/api/lulu/price-estimate", selectedConfig, shippingLevel, quantity, shippingAddress.country_code],
    queryFn: async () => {
      const config = bookConfigs?.configs.find(c => c.id.toString() === selectedConfig);
      if (!config) throw new Error("No configuration selected");
      
      const response = await apiRequest("POST", "/api/lulu/price-estimate", {
        podPackageId: config.podPackageId,
        quantity,
        shippingCountry: shippingAddress.country_code || "US",
        shippingLevel,
      });
      return response.json();
    },
    enabled: !!selectedConfig && !!bookConfigs,
  });

  // Submit print order
  const submitOrder = useMutation({
    mutationFn: async () => {
      if (!selectedConfig) throw new Error("Please select a book configuration");
      
      // Final validation
      const requiredFields = ["name", "address_line1", "city", "postal_code"];
      for (const field of requiredFields) {
        if (!shippingAddress[field as keyof ShippingAddress]) {
          throw new Error(`Please fill in all required shipping fields`);
        }
      }
      
      if (Object.keys(formErrors).length > 0) {
        throw new Error("Please fix all form errors before submitting");
      }
      
      const response = await apiRequest("POST", "/api/lulu/submit-order", {
        storyId,
        bookConfigId: parseInt(selectedConfig),
        shippingAddress,
        shippingLevel,
        bookType,
        quantity,
        isGift,
        giftMessage: isGift ? giftMessage : undefined,
      });
      return response.json();
    },
    onSuccess: (data) => {
      toast({
        title: "📚 Book Order Submitted!",
        description: `Your ${quantity > 1 ? `${quantity} books have` : 'book has'} been sent to print. ${isGift ? '🎁 Gift order' : 'Estimated delivery'}: 7-10 business days.`,
        duration: 6000,
      });
      setIsOpen(false);
      queryClient.invalidateQueries({ queryKey: ["/api/lulu/my-print-orders"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Order Failed",
        description: error.message || "Failed to submit print order. Please try again.",
        variant: "destructive",
      });
    },
  });

  const selectedBookConfig = bookConfigs?.configs.find(c => c.id.toString() === selectedConfig);

  // Calculate total price
  const calculateTotal = () => {
    if (!selectedBookConfig || !priceEstimate) return 0;
    const bookPrice = parseFloat(selectedBookConfig.retailPrice) * quantity;
    const shipping = priceEstimate.estimate.shipping_cost;
    return bookPrice + shipping;
  };

  // Progress indicator
  const getStepNumber = (step: OrderStep) => {
    switch (step) {
      case 'book-config': return 1;
      case 'shipping': return 2;
      case 'review': return 3;
      default: return 1;
    }
  };

  const steps = [
    { key: 'book-config', title: 'Book Details', icon: Book },
    { key: 'shipping', title: 'Shipping Info', icon: MapPin },
    { key: 'review', title: 'Review Order', icon: CheckCircle },
  ];

  // Handle input changes with validation
  const handleAddressChange = (field: keyof ShippingAddress, value: string) => {
    let formattedValue = value;
    
    // Auto-format postal code
    if (field === 'postal_code') {
      formattedValue = formatPostalCode(value, shippingAddress.country_code);
    }
    
    // Auto-format phone number (US format only)
    if (field === 'phone' && shippingAddress.country_code === 'US') {
      const digits = value.replace(/\D/g, '');
      if (digits.length <= 10) {
        formattedValue = digits.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
      } else {
        formattedValue = value; // Keep original for international numbers
      }
    } else if (field === 'phone') {
      // For non-US countries, allow any format but remove excessive whitespace
      formattedValue = value.replace(/\s+/g, ' ').trim();
    }
    
    setShippingAddress({ ...shippingAddress, [field]: formattedValue });
    validateField(field, formattedValue);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button className="bg-gradient-to-r from-purple-600 to-teal-600 hover:from-purple-700 hover:to-teal-700 text-white" data-testid="order-book-button">
          <Book className="mr-2 h-4 w-4" />
          Order Printed Book
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[95vh] overflow-hidden flex flex-col">
        <DialogHeader className="shrink-0">
          <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-teal-600 bg-clip-text text-transparent">
            Order Your Story Book
          </DialogTitle>
          <DialogDescription className="text-base">
            Transform "{storyTitle}" into a beautiful printed keepsake
          </DialogDescription>
          
          {/* Progress Indicator */}
          <div className="flex items-center justify-between mt-6 px-4">
            {steps.map((step, index) => {
              const isActive = currentStep === step.key;
              const isCompleted = getStepNumber(currentStep) > getStepNumber(step.key as OrderStep);
              const StepIcon = step.icon;
              
              return (
                <div key={step.key} className="flex items-center flex-1">
                  <div className="flex flex-col items-center">
                    <div className={`
                      w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all
                      ${isActive ? 'bg-purple-600 border-purple-600 text-white' : 
                        isCompleted ? 'bg-green-600 border-green-600 text-white' : 
                        'bg-gray-100 border-gray-300 text-gray-500'}
                    `}>
                      {isCompleted ? <CheckCircle className="w-5 h-5" /> : <StepIcon className="w-5 h-5" />}
                    </div>
                    <span className={`mt-2 text-sm font-medium ${
                      isActive ? 'text-purple-600' : isCompleted ? 'text-green-600' : 'text-gray-500'
                    }`}>
                      {step.title}
                    </span>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`h-1 flex-1 mx-4 rounded ${
                      isCompleted ? 'bg-green-600' : 'bg-gray-200'
                    }`} />
                  )}
                </div>
              );
            })}
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-1">
          <div className="space-y-6 mt-6">
            {/* Step 1: Book Configuration */}
            {currentStep === 'book-config' && (
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Book className="w-5 h-5" />
                      Book Configuration
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Book Type Selection */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div 
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          bookType === 'storybook' ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => setBookType('storybook')}
                      >
                        <div className="flex items-center gap-3">
                          <Book className="w-6 h-6 text-purple-600" />
                          <div>
                            <h3 className="font-semibold">Full Storybook</h3>
                            <p className="text-sm text-gray-600">Complete story with text and illustrations</p>
                          </div>
                        </div>
                      </div>
                      
                      <div 
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          bookType === 'coloring' ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => setBookType('coloring')}
                      >
                        <div className="flex items-center gap-3">
                          <Package className="w-6 h-6 text-teal-600" />
                          <div>
                            <h3 className="font-semibold">Coloring Book</h3>
                            <p className="text-sm text-gray-600">Simple text with outline drawings</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Format Selection */}
                    <div>
                      <Label htmlFor="book-config" className="text-base font-semibold">Choose Book Format</Label>
                      {configsLoading ? (
                        <div className="flex items-center justify-center p-8">
                          <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
                          <span className="ml-2">Loading book formats...</span>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 gap-3 mt-3">
                          {bookConfigs?.configs.map((config) => (
                            <div
                              key={config.id}
                              className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                                selectedConfig === config.id.toString() 
                                  ? 'border-purple-500 bg-purple-50' 
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                              onClick={() => setSelectedConfig(config.id.toString())}
                            >
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <h4 className="font-medium text-lg">{config.name}</h4>
                                  <div className="flex flex-wrap gap-2 mt-2">
                                    <Badge variant="secondary">{config.size}</Badge>
                                    <Badge variant="secondary">{config.binding}</Badge>
                                    <Badge variant="secondary">{config.pages} pages</Badge>
                                    <Badge variant="secondary">{config.color}</Badge>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <div className="text-2xl font-bold text-purple-600">${config.retailPrice}</div>
                                  <div className="text-sm text-gray-500">per book</div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Quantity and Gift Options */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label className="text-base font-semibold">Quantity</Label>
                        <div className="flex items-center gap-3 mt-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setQuantity(Math.max(1, quantity - 1))}
                            disabled={quantity <= 1}
                            data-testid="button-quantity-minus"
                          >
                            <Minus className="w-4 h-4" />
                          </Button>
                          <span className="text-xl font-semibold w-8 text-center" data-testid="text-quantity">{quantity}</span>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setQuantity(Math.min(10, quantity + 1))}
                            disabled={quantity >= 10}
                            data-testid="button-quantity-plus"
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>

                      <div>
                        <Label className="text-base font-semibold flex items-center gap-2">
                          <Gift className="w-4 h-4" />
                          Gift Order
                        </Label>
                        <div className="mt-2">
                          <Button
                            type="button"
                            variant={isGift ? "default" : "outline"}
                            onClick={() => setIsGift(!isGift)}
                            className={isGift ? "bg-gradient-to-r from-purple-600 to-teal-600" : ""}
                            data-testid="button-gift-toggle"
                          >
                            {isGift ? 'Yes, this is a gift' : 'No, personal order'}
                          </Button>
                          {isGift && (
                            <Input
                              placeholder="Optional gift message"
                              value={giftMessage}
                              onChange={(e) => setGiftMessage(e.target.value)}
                              className="mt-2"
                              maxLength={200}
                              data-testid="input-gift-message"
                            />
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Quick Preview */}
                    {selectedBookConfig && (
                      <Card className="bg-gradient-to-r from-purple-50 to-teal-50 border-purple-200">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-lg flex items-center gap-2">
                            <Eye className="w-5 h-5" />
                            Order Preview
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <div className="font-medium text-gray-700">Book Type</div>
                              <div className="capitalize">{bookType}</div>
                            </div>
                            <div>
                              <div className="font-medium text-gray-700">Format</div>
                              <div>{selectedBookConfig.size}</div>
                            </div>
                            <div>
                              <div className="font-medium text-gray-700">Quantity</div>
                              <div>{quantity} {quantity === 1 ? 'book' : 'books'}</div>
                            </div>
                            <div>
                              <div className="font-medium text-gray-700">Estimated Cost</div>
                              <div className="text-lg font-bold text-purple-600" data-testid="text-estimated-cost">
                                ${(parseFloat(selectedBookConfig.retailPrice) * quantity).toFixed(2)}
                              </div>
                              <div className="text-xs text-gray-500">+ shipping</div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Step 2: Shipping Information */}
            {currentStep === 'shipping' && (
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <MapPin className="w-5 h-5" />
                      Shipping Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Contact Information */}
                    <div className="space-y-4">
                      <h4 className="font-semibold text-gray-800">Contact Details</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="name">Full Name *</Label>
                          <Input
                            id="name"
                            value={shippingAddress.name}
                            onChange={(e) => handleAddressChange('name', e.target.value)}
                            className={formErrors.name ? 'border-red-500' : ''}
                            data-testid="input-shipping-name"
                          />
                          {formErrors.name && (
                            <p className="text-sm text-red-500 mt-1">{formErrors.name}</p>
                          )}
                        </div>
                        
                        <div>
                          <Label htmlFor="email">Email Address</Label>
                          <Input
                            id="email"
                            type="email"
                            value={shippingAddress.email}
                            onChange={(e) => handleAddressChange('email', e.target.value)}
                            className={formErrors.email ? 'border-red-500' : ''}
                            placeholder="your@email.com"
                            data-testid="input-shipping-email"
                          />
                          {formErrors.email && (
                            <p className="text-sm text-red-500 mt-1">{formErrors.email}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    <Separator />
                    
                    {/* Address Information */}
                    <div className="space-y-4">
                      <h4 className="font-semibold text-gray-800">Delivery Address</h4>
                      
                      <div>
                        <Label htmlFor="address1">Street Address *</Label>
                        <Input
                          id="address1"
                          value={shippingAddress.address_line1}
                          onChange={(e) => handleAddressChange('address_line1', e.target.value)}
                          className={formErrors.address_line1 ? 'border-red-500' : ''}
                          placeholder="123 Main Street"
                          data-testid="input-address1"
                        />
                        {formErrors.address_line1 && (
                          <p className="text-sm text-red-500 mt-1">{formErrors.address_line1}</p>
                        )}
                      </div>
                      
                      <div>
                        <Label htmlFor="address2">Apartment, Suite, etc.</Label>
                        <Input
                          id="address2"
                          value={shippingAddress.address_line2}
                          onChange={(e) => handleAddressChange('address_line2', e.target.value)}
                          placeholder="Apt 4B, Suite 100"
                          data-testid="input-address2"
                        />
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <Label htmlFor="city">City *</Label>
                          <Input
                            id="city"
                            value={shippingAddress.city}
                            onChange={(e) => handleAddressChange('city', e.target.value)}
                            className={formErrors.city ? 'border-red-500' : ''}
                            placeholder="San Francisco"
                            data-testid="input-city"
                          />
                          {formErrors.city && (
                            <p className="text-sm text-red-500 mt-1">{formErrors.city}</p>
                          )}
                        </div>
                        
                        <div>
                          <Label htmlFor="state">State/Province</Label>
                          <Input
                            id="state"
                            value={shippingAddress.state_code}
                            onChange={(e) => handleAddressChange('state_code', e.target.value)}
                            placeholder="CA"
                            maxLength={3}
                            data-testid="input-state"
                          />
                        </div>
                        
                        <div>
                          <Label htmlFor="postal">Postal Code *</Label>
                          <Input
                            id="postal"
                            value={shippingAddress.postal_code}
                            onChange={(e) => handleAddressChange('postal_code', e.target.value)}
                            className={formErrors.postal_code ? 'border-red-500' : ''}
                            placeholder={shippingAddress.country_code === 'CA' ? 'A1A 1A1' : '12345'}
                            data-testid="input-postal"
                          />
                          {formErrors.postal_code && (
                            <p className="text-sm text-red-500 mt-1">{formErrors.postal_code}</p>
                          )}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="country">Country</Label>
                          <Select 
                            value={shippingAddress.country_code} 
                            onValueChange={(value) => {
                              setShippingAddress({ ...shippingAddress, country_code: value, postal_code: '' });
                              setFormErrors({ ...formErrors, postal_code: '' });
                            }}
                          >
                            <SelectTrigger id="country" data-testid="select-country">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="US">🇺🇸 United States</SelectItem>
                              <SelectItem value="CA">🇨🇦 Canada</SelectItem>
                              <SelectItem value="GB">🇬🇧 United Kingdom</SelectItem>
                              <SelectItem value="AU">🇦🇺 Australia</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div>
                          <Label htmlFor="phone">Phone Number</Label>
                          <Input
                            id="phone"
                            type="tel"
                            value={shippingAddress.phone}
                            onChange={(e) => handleAddressChange('phone', e.target.value)}
                            className={formErrors.phone ? 'border-red-500' : ''}
                            placeholder={shippingAddress.country_code === 'US' ? '(555) 123-4567' : '+1 234 567 8900'}
                            data-testid="input-phone"
                          />
                          {formErrors.phone && (
                            <p className="text-sm text-red-500 mt-1">{formErrors.phone}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    <Separator />

                    {/* Shipping Options */}
                    <div className="space-y-4">
                      <h4 className="font-semibold text-gray-800">Shipping Speed</h4>
                      <div className="grid grid-cols-1 gap-3">
                        {[
                          { value: 'Mail', title: 'Standard Mail', time: '7-10 business days', icon: '📮', price: 'Lowest cost' },
                          { value: 'Priority', title: 'Priority Mail', time: '3-5 business days', icon: '✈️', price: 'Moderate cost' },
                          { value: 'Express', title: 'Express Mail', time: '1-2 business days', icon: '🚀', price: 'Higher cost' },
                        ].map((option) => (
                          <div
                            key={option.value}
                            className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                              shippingLevel === option.value 
                                ? 'border-purple-500 bg-purple-50' 
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                            onClick={() => setShippingLevel(option.value)}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <span className="text-2xl">{option.icon}</span>
                                <div>
                                  <h5 className="font-medium">{option.title}</h5>
                                  <p className="text-sm text-gray-600">{option.time}</p>
                                </div>
                              </div>
                              <div className="text-right">
                                <Badge variant={shippingLevel === option.value ? 'default' : 'secondary'}>
                                  {option.price}
                                </Badge>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Step 3: Review Order */}
            {currentStep === 'review' && (
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      Review Your Order
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Order Summary */}
                    <div className="space-y-4">
                      <h4 className="font-semibold text-gray-800">Book Details</h4>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <div className="text-sm text-gray-600">Story Title</div>
                            <div className="font-medium">{storyTitle}</div>
                          </div>
                          <div>
                            <div className="text-sm text-gray-600">Book Type</div>
                            <div className="font-medium capitalize">{bookType}</div>
                          </div>
                          {selectedBookConfig && (
                            <>
                              <div>
                                <div className="text-sm text-gray-600">Format</div>
                                <div className="font-medium">{selectedBookConfig.name}</div>
                              </div>
                              <div>
                                <div className="text-sm text-gray-600">Specifications</div>
                                <div className="font-medium">
                                  {selectedBookConfig.size} • {selectedBookConfig.binding} • {selectedBookConfig.pages} pages
                                </div>
                              </div>
                            </>
                          )}
                          <div>
                            <div className="text-sm text-gray-600">Quantity</div>
                            <div className="font-medium">{quantity} {quantity === 1 ? 'book' : 'books'}</div>
                          </div>
                          {isGift && (
                            <div>
                              <div className="text-sm text-gray-600">Gift Order</div>
                              <div className="font-medium flex items-center gap-1">
                                <Gift className="w-4 h-4" />
                                Yes {giftMessage && `• "${giftMessage}"`}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <Separator />

                    {/* Shipping Details */}
                    <div className="space-y-4">
                      <h4 className="font-semibold text-gray-800">Shipping Details</h4>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <div className="text-sm text-gray-600">Recipient</div>
                            <div className="font-medium">{shippingAddress.name}</div>
                            {shippingAddress.email && (
                              <div className="text-sm text-gray-600">{shippingAddress.email}</div>
                            )}
                          </div>
                          <div>
                            <div className="text-sm text-gray-600">Delivery Address</div>
                            <div className="font-medium">
                              {shippingAddress.address_line1}
                              {shippingAddress.address_line2 && `, ${shippingAddress.address_line2}`}
                            </div>
                            <div className="text-sm text-gray-600">
                              {shippingAddress.city}, {shippingAddress.state_code} {shippingAddress.postal_code}
                            </div>
                            <div className="text-sm text-gray-600">
                              {shippingAddress.country_code === 'US' ? 'United States' : 
                               shippingAddress.country_code === 'CA' ? 'Canada' : 
                               shippingAddress.country_code === 'GB' ? 'United Kingdom' : 
                               shippingAddress.country_code === 'AU' ? 'Australia' : shippingAddress.country_code}
                            </div>
                          </div>
                          <div>
                            <div className="text-sm text-gray-600">Shipping Speed</div>
                            <div className="font-medium">
                              {shippingLevel === 'Mail' ? 'Standard Mail (7-10 days)' :
                               shippingLevel === 'Priority' ? 'Priority Mail (3-5 days)' :
                               'Express Mail (1-2 days)'}
                            </div>
                          </div>
                          {shippingAddress.phone && (
                            <div>
                              <div className="text-sm text-gray-600">Phone</div>
                              <div className="font-medium">{shippingAddress.phone}</div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <Separator />

                    {/* Price Breakdown */}
                    {selectedConfig && priceEstimate && (
                      <div className="space-y-4">
                        <h4 className="font-semibold text-gray-800">Order Total</h4>
                        <Card className="border-2 border-purple-200">
                          <CardContent className="p-4">
                            <div className="space-y-3">
                              <div className="flex justify-between items-center">
                                <span className="text-gray-600">
                                  {bookType} × {quantity}
                                </span>
                                <span className="font-medium">
                                  ${(parseFloat(selectedBookConfig?.retailPrice || "0") * quantity).toFixed(2)}
                                </span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-gray-600">
                                  {shippingLevel === 'Mail' ? 'Standard' : 
                                   shippingLevel === 'Priority' ? 'Priority' : 'Express'} Shipping
                                </span>
                                <span className="font-medium">
                                  ${priceEstimate.estimate.shipping_cost.toFixed(2)}
                                </span>
                              </div>
                              <Separator />
                              <div className="flex justify-between items-center">
                                <span className="text-lg font-semibold">Total</span>
                                <span className="text-2xl font-bold text-purple-600" data-testid="text-order-total">
                                  ${calculateTotal().toFixed(2)}
                                </span>
                              </div>
                              <div className="text-sm text-gray-500 text-center">
                                Estimated delivery: {shippingLevel === 'Express' ? '1-2' : shippingLevel === 'Priority' ? '3-5' : '7-10'} business days
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

          </div>
        </div>

        {/* Action Buttons */}
        <div className="shrink-0 border-t bg-gray-50 px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => setIsOpen(false)}
                disabled={submitOrder.isPending}
              >
                Cancel
              </Button>
              {currentStep !== 'book-config' && (
                <Button
                  variant="outline"
                  onClick={previousStep}
                  disabled={submitOrder.isPending}
                  data-testid="button-previous-step"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back
                </Button>
              )}
            </div>

            <div className="flex items-center gap-4">
              {/* Price indicator */}
              {selectedConfig && priceEstimate && currentStep !== 'book-config' && (
                <div className="text-right">
                  <div className="text-sm text-gray-600">Total</div>
                  <div className="text-xl font-bold text-purple-600">
                    ${calculateTotal().toFixed(2)}
                  </div>
                </div>
              )}

              {currentStep === 'review' ? (
                <Button
                  onClick={() => submitOrder.mutate()}
                  disabled={!canProceedToNextStep() || submitOrder.isPending}
                  className="bg-gradient-to-r from-purple-600 to-teal-600 hover:from-purple-700 hover:to-teal-700 text-white min-w-[140px]"
                  data-testid="place-order-button"
                >
                  {submitOrder.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Truck className="mr-2 h-4 w-4" />
                      Place Order
                    </>
                  )}
                </Button>
              ) : (
                <Button
                  onClick={nextStep}
                  disabled={!canProceedToNextStep()}
                  className="bg-gradient-to-r from-purple-600 to-teal-600 hover:from-purple-700 hover:to-teal-700 text-white min-w-[120px]"
                  data-testid="button-next-step"
                >
                  Next
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}